import React, { Fragment, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Alert from "react-bootstrap/Alert";
import AxisSpinner from "./AxisSpinner";
import { UserLink } from "../util/Links";
import { ButtonModalFormSaveClose } from "./ButtonModalSelection";
import { BsPerson, BsExclamationTriangle, BsXCircle } from "react-icons/bs";

const CancelShift = ({ shift, onComplete, action = null, close = null }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [user, setUser] = useState(null);
  const [needRejectReason, setNeedRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonValid, setRejectReasonValid] = useState(false);
  const [cancelAction, setCancelAction] = useState("CANCEL");
  const [actionSelectionDisabled, setActionSelectionDisabled] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (shift === null && shift === undefined) return;

    setUser(shift?.user);
  }, [shift]);

  useEffect(() => {
    if (action === null || action === undefined) return;

    setCancelAction(action);
    setActionSelectionDisabled(true);
  }, [action]);

  useEffect(() => {
    setNeedRejectReason(
      user !== null &&
        user !== undefined &&
        user?.id !== null &&
        user?.id !== undefined,
    );
  }, [user]);

  useEffect(() => {
    var valid =
      rejectReason !== null &&
      rejectReason !== undefined &&
      rejectReason.trim().length > 0;
    setRejectReasonValid(valid);
  }, [rejectReason]);

  useEffect(() => {
    var valid = true;
    if (needRejectReason) valid = rejectReasonValid;
    setSaveDisabled(!valid);
  }, [user, rejectReason, needRejectReason, rejectReasonValid]);

  const onSave = async (e) => {
    e.preventDefault();
    
    // Reset error state
    setError(null);
    setShowError(false);
    
    try {
      setLoading(true);

      var params = {
        shift_id: shift?.id,
        cancel_action: cancelAction,
        reject_reason: rejectReason,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };
      const response = await authAxios().post(
        "/api/backoffice/shifts/" +
          (shift?.table === "shifts"
            ? "cancel_active_shift"
            : "delete_archive_booking"),
        null,
        config,
      );

      const fetchConfig = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          shift_id: shift?.id,
        },
      };

      const shiftResponse = await authAxios().get(
        "/api/backoffice/shifts/" +
          (shift?.table === "shifts" ? "active" : "archive"),
        fetchConfig,
      );

      setLoading(false);
      onComplete(shiftResponse.data);
    } catch (err) {
      setLoading(false);
      
      // Set error message based on the error
      let errorMessage = "Failed to cancel shift. Please try again.";
      
      if (err.response) {
        if (err.response.status === 400) {
          errorMessage = "Invalid cancellation data. Please check all fields and try again.";
        } else if (err.response.status === 401) {
          errorMessage = "Your session has expired. Please log in again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to cancel this shift.";
        } else if (err.response.status === 404) {
          errorMessage = "The shift was not found.";
        } else if (err.response.status === 500) {
          errorMessage = "Server error while processing your request. Please try again later.";
        }
        
        // If we have a more specific error message from the server
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      
      setError(errorMessage);
      setShowError(true);
      console.error("Error cancelling shift:", err);
    }
  };

  const rejectReasonChanged = (value) => {
    var text = value.target.value;
    setRejectReason(text.trim());
  };

  const cancelActionChanged = (value) => {
    var text = value.target.value;
    setCancelAction(text);
  };

  return (
    <>
      {loading && (
        <div className="overlay-spinner">
          <div className="text-center">
            <AxisSpinner loading={loading} />
            <div className="mt-2 fw-bold text-axis-offblack">
              {cancelAction === "CANCEL" ? "Cancelling" : "Reopening"} Shift...
            </div>
          </div>
        </div>
      )}
      
      <Form onSubmit={onSave}>
        {/* Error Alert */}
        {showError && error && (
          <Alert 
            variant="danger" 
            className="mb-4" 
            dismissible 
            onClose={() => setShowError(false)}
          >
            <div className="d-flex align-items-center">
              <BsExclamationTriangle className="me-2" size={20} />
              <div>{error}</div>
            </div>
          </Alert>
        )}
        
        {/* Header with shift info */}
        <div className="mb-4 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-axis-offblack">
            Are you sure you want to {cancelAction.toLowerCase()} this shift?
          </h5>
          <div className="text-muted small">
            {shift?.facility?.name && <span>Facility: {shift?.facility?.name} | </span>}
            {shift?.ward?.identifier && <span>Ward: {shift?.ward?.identifier}</span>}
          </div>
        </div>
        
        {/* Cancel Options Card */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-light d-flex align-items-center">
            <BsXCircle className="me-2 text-primary" />
            <span className="fw-bold">Cancellation Options</span>
          </Card.Header>
          <Card.Body>
            {shift?.table === "shifts" && action === null && (
              <Form.Group className="mb-3" controlId="formCancelAction">
                <Form.Label className="fw-semibold">Action to Take</Form.Label>
                <Form.Select
                  value={cancelAction}
                  onChange={cancelActionChanged}
                  disabled={actionSelectionDisabled}
                  className="mb-2"
                >
                  <option value="REOPEN">Reopen Shift</option>
                  <option value="CANCEL">Cancel Shift</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Select whether to cancel or reopen this shift
                </Form.Text>
              </Form.Group>
            )}
            
            {user !== null && user !== undefined && user?.id !== null && user?.id !== undefined && (
              <Form.Group className="mb-3" controlId="formUserSelection">
                <Form.Label className="fw-semibold">Assigned User</Form.Label>
                <div className="border p-2 rounded bg-light">
                  <div className="d-flex align-items-center">
                    <BsPerson className="me-2 text-primary" size={18} />
                    {UserLink(user)}
                  </div>
                </div>
                <Form.Text className="text-muted">
                  This user is currently assigned to the shift
                </Form.Text>
              </Form.Group>
            )}
            
            {needRejectReason && (
              <Form.Group className="mb-3" controlId="formRejectReason">
                <Form.Label className="fw-semibold">Rejection Reason <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  onChange={rejectReasonChanged}
                  isValid={rejectReasonValid}
                  isInvalid={!rejectReasonValid}
                  required
                  placeholder={`Enter reason for cancelling this shift`}
                />
                <Form.Text className="text-muted">
                  Provide a reason why this shift is being cancelled
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  Please provide a reason for cancelling this shift
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Card.Body>
        </Card>
        
        {/* Action Buttons */}
        <div className="text-end mt-4">
          <ButtonModalFormSaveClose
            close={close}
            formSave={onSave}
            saveName={cancelAction === "CANCEL" ? "Cancel Shift" : "Reopen Shift"}
            loading={saveDisabled || loading}
          />
        </div>
      </Form>
    </>
  );
};

export default CancelShift;
